"use client";

import { useContext, useCallback } from "react";
import { API_URL } from "@/components/config/config";
import { AppStateContext } from "@/components/context/AppContext";
import { usePathname, useRouter } from "next/navigation";
import { isFreeToolPath } from "@/lib/free-tools/registry";
import { isOpenWhenSignedIn } from "@/lib/publicPages";
import fetchWithAuth from "./fetchWithAuth";

const PROFILE_CACHE_KEY = "aiia_profile_cache";
const PROFILE_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

/** Read cached profile from sessionStorage (survives refresh, not new tabs). */
function getCachedProfile() {
    try {
        const raw = sessionStorage.getItem(PROFILE_CACHE_KEY);
        if (!raw) return null;
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts > PROFILE_CACHE_TTL) {
            sessionStorage.removeItem(PROFILE_CACHE_KEY);
            return null;
        }
        return data;
    } catch {
        return null;
    }
}

/** Write profile to sessionStorage with timestamp. */
function setCachedProfile(data) {
    try {
        sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
    } catch { /* quota exceeded — ignore */ }
}

/** Clear cache on logout. */
export function clearProfileCache() {
    try { sessionStorage.removeItem(PROFILE_CACHE_KEY); } catch {}
}

export const useFetchUserDetails = () => {
    const pathname = usePathname();
    const { setIsUserFetching, setUserProfile } = useContext(AppStateContext);
    const router = useRouter();
    // Signed-in users may browse the marketing homepage and content pages;
    // only non-public app paths auto-route to the dashboard. The list of
    // public pages is lib/publicPages.js.
    const isPublicPath = isOpenWhenSignedIn(pathname);
    // The free tools (/tools + one page per implemented tool) are public and
    // must survive a signed-in visit, so they are checked against the CURRENT
    // window.location.pathname at every redirect decision rather than against
    // `pathname` above: fetchUserDetails is memoized on [router], so the
    // render-time `isPublicPath` it closes over can be stale by the time the
    // profile lands — a visitor who navigated to a tool mid-fetch would
    // otherwise be ejected to the dashboard. Every other route — dashboard,
    // auth, and the public page list, keeps the behaviour it has today.

    // The candidate viewing page gets the same current-address check: an old
    // candidate dashboard link sends the visitor there while the profile is
    // still loading, and a recruiter must not be pulled off it afterwards.
    const isViewingPage = (path) => path.startsWith("/my-interviews");

    const withTimeout = (promise, ms = 5000) =>
        Promise.race([
            promise,
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error("Request timeout")), ms)
            ),
        ]);

    const fetchUserDetails = useCallback(async (user) => {
        if (!user?.uid) {
            router.push("/auth/login");
            return;
        }

        try {
            // ── STEP 1: Serve cached profile instantly (no DB wait) ──
            const cached = getCachedProfile();
            if (cached && cached.uid === user.uid) {
                setUserProfile(cached);
                setIsUserFetching(false);

                // Route immediately from cache
                const path = window.location.pathname;
                const cachedDashPath = cached.role === "hr" ? "/dashboard/agency" : "/my-interviews";
                if (cachedDashPath && !path.startsWith(cachedDashPath) && !isPublicPath && !isFreeToolPath(path) && !isViewingPage(path) && !path.startsWith("/pricing")) {
                    router.push(cachedDashPath);
                }

                // Revalidate in background (don't block UI)
                _revalidateProfile(user).catch(() => {});
                return;
            }

            // ── STEP 2: No cache — fetch from DB (first login / cache expired) ──
            setIsUserFetching(true);

            // Phase 3E: org fetch removed — onboarding flow killed.
            // Trailing slashes are required: Django redirects (301) without
            // them, with a relative Location header that the browser
            // resolves against localhost:3000 → SPA fallback → HTML → JSON.parse boom.
            const profileUrl = `${API_URL}/profiles/${user.uid}/`;
            const subuserUrl = `${API_URL}/subuser/${user.uid}/`;
            const [profileResult, subuserResult] = await Promise.allSettled([
                withTimeout(fetchWithAuth(profileUrl)),
                withTimeout(fetchWithAuth(subuserUrl)),
            ]);

            let profileData = null;
            let dashboardPath = ""
            let isSubuser = false;
            // Handle profile response
            if (profileResult.status === "fulfilled" && profileResult.value.ok) {
                profileData = await profileResult.value.json();
                setUserProfile(profileData);
                setCachedProfile(profileData);
                dashboardPath =
                    profileData?.role === "hr"
                        ? "/dashboard/agency"
                        : "/my-interviews";
            }
            // Optional: handle subuser data if needed
            if (subuserResult.status === "fulfilled" && subuserResult.value.ok) {
                profileData = await subuserResult.value.json();
                setUserProfile(profileData.data);
                setCachedProfile(profileData.data);
                isSubuser = true
                dashboardPath = "/dashboard/agency"
            }
            const isLocalProxyMode =
                process.env.NODE_ENV !== "production" &&
                String(process.env.NEXT_PUBLIC_API_URL || "").startsWith("/api/proxy/");
            const isDisableAuthMode =
                String(process.env.NEXT_PUBLIC_DISABLE_AUTH || "").toLowerCase() === "true";

            // Dev fallback: if profile/subuser endpoints return nothing (e.g. the
            // probe user has no Profile row yet), keep the app usable with a
            // minimal stub profile so the recruiter UI still renders.
            if (!profileData && (isLocalProxyMode || isDisableAuthMode)) {
                const nameParts = String(user?.displayName || "").trim().split(" ").filter(Boolean);
                const firstName = nameParts[0] || "Local";
                const lastName = nameParts.slice(1).join(" ") || "User";

                profileData = {
                    uid: user.uid,
                    role: "hr",
                    first_name: firstName,
                    last_name: lastName,
                    email: user?.email || "",
                    plan: { id: 1, free_seconds: 3600 },
                    free_seconds_used_today: 0,
                    seconds_used: 0,
                };

                setUserProfile(profileData);
                dashboardPath = "/dashboard/agency";
            }

            // Routing logic (only dependent on profile data)
            const path = window.location.pathname;

            if (path.startsWith("/pricing")) return;

            if (profileData && !profileData?.role && !isSubuser) {
                // Phase 3E: /choice (role selection) flow was removed —
                // every user now has a role at signup. If we ever land here
                // with a role-less profile, send them to / (the public
                // landing page, which then routes via ContextGate).
                if (path !== "/" && !isFreeToolPath(path)) {
                    router.push("/");
                }
                return;
            }

            if (dashboardPath && !path.startsWith(dashboardPath) && !isPublicPath && !isFreeToolPath(path) && !isViewingPage(path)) {
                router.push(dashboardPath);
            }

        } catch (err) {
            console.error("Error fetching user details:", err);
            // If we have a cached profile, don't kick user out — use it
            const fallback = getCachedProfile();
            if (fallback && fallback.uid === user.uid) {
                setUserProfile(fallback);
                return;
            }
            if (isFreeToolPath(window.location.pathname)) return;
            router.push("/");
        } finally {
            setIsUserFetching(false);
        }
    }, [router]);

    /** Background revalidation — updates cache + context without blocking UI. */
    const _revalidateProfile = useCallback(async (user) => {
        try {
            const profileUrl = `${API_URL}/profiles/${user.uid}/`;
            const subuserUrl = `${API_URL}/subuser/${user.uid}/`;
            const [profileResult, subuserResult] = await Promise.allSettled([
                fetchWithAuth(profileUrl),
                fetchWithAuth(subuserUrl),
            ]);

            if (subuserResult.status === "fulfilled" && subuserResult.value.ok) {
                const data = await subuserResult.value.json();
                setUserProfile(data.data);
                setCachedProfile(data.data);
                return;
            }

            if (profileResult.status === "fulfilled" && profileResult.value.ok) {
                const data = await profileResult.value.json();
                setUserProfile(data);
                setCachedProfile(data);
            }
        } catch {
            // Background — don't disrupt user
        }
    }, [setUserProfile]);

    return { fetchUserDetails };
};
